import { lazy, Suspense } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';

import type {
  SliderDynamicAttributes,
  SliderItemAttributes,
  SLIDER_TYPES,
} from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/types';
import LoginCard from 'hb-builder/components/common/LoginCard';
import EmptyDynamicState from 'hb-builder/components/common/state/EmptyDynamicState';
import ErrorState from 'hb-builder/components/common/state/ErrorState';
import SuspenseFallback from 'hb-builder/components/common/SuspenseFallback';
import type { AuthenticationIds } from 'hb-builder/types/config/AuthenticationIds';
import { useGetDynamicSearch } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicSearch';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import { APP_TYPE } from 'hb-react/types/appType';

const SelectedLayout = {
  discussions: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/DiscussionsWrapper'
      ),
  ),
  events: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/EventsWrapper'
      ),
  ),
  topics: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper'
      ),
  ),
  news: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/NewsWrapper'
      ),
  ),
  posts: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/PostsWrapper'
      ),
  ),
  users: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/UsersLaneWrapper'
      ),
  ),
};

const NEED_TO_LOGIN_CONTENT_TYPES = ['discussions', 'posts', 'users'];

const DATA_TYPES = Object.freeze({
  discussions: 'dataDiscussionsType',
  events: 'dataType',
  news: 'dataType',
  topics: 'dataTopicType',
  posts: 'dataPostsType',
  users: 'dataUserType',
});

export interface SliderLayoutsProps {
  app: APP_TYPE;
  dynamicAttributes?: SliderDynamicAttributes;
  ids?: AuthenticationIds;
  isPublic: boolean;
  item: DocumentItem<SliderItemAttributes>;
  navigate?: NavigateFunction;
  networkRootUrl?: string | null;
  type: Exclude<SLIDER_TYPES, 'empty'>;
  whiteLabelUrl?: string | null;
}

const SliderLayouts = ({
  app,
  ids,
  isPublic,
  item,
  navigate,
  networkRootUrl,
  type,
  whiteLabelUrl,
}: SliderLayoutsProps) => {
  const dynamicAttributes = item.attributes?.dynamicAttributes;
  const isBackOffice = app === APP_TYPE.BACKOFFICE;
  const isPublicOffice = app === APP_TYPE.PUBLIC_OFFICE;
  const dataType = dynamicAttributes && DATA_TYPES[dynamicAttributes?.type];

  const queryDataType = dataType && dynamicAttributes?.query[dataType];

  const params = {
    query: {
      page_visibility: isPublic ? 'public' : 'private',
      page: 1,
      per_page: dynamicAttributes?.query.numberOfItems,
      total_items: dynamicAttributes?.query.numberOfItems,
      type: queryDataType,
      categories: dynamicAttributes?.query?.categories,
      user_id: ids?.user,
      ...(type === 'posts' && { published: true }),
    },
  };

  const isFetchEnabled =
    !NEED_TO_LOGIN_CONTENT_TYPES.includes(type) || !isPublicOffice;

  const { data, error, isError, isLoading } = useGetDynamicSearch({
    app,
    enabled: isFetchEnabled,
    params,
    type,
    whiteLabelUrl,
  });

  if (!isFetchEnabled) {
    return <LoginCard componentName={type} />;
  }

  if (isError) {
    return isBackOffice ? <ErrorState error={error} /> : null;
  }

  if (data?.length === 0 && !isLoading) {
    return isBackOffice ? (
      <EmptyDynamicState component="slider" type={type} />
    ) : null;
  }

  if (isLoading) {
    return <Loader />;
  }

  const Layout = SelectedLayout[type];

  const customProps = {
    app,
    data,
    ids,
    isLoading,
    item,
    navigate,
    networkRootUrl,
    params,
    whiteLabelUrl,
  };

  return (
    <Suspense fallback={<SuspenseFallback />}>
      {/* @TODO type this */}
      <Layout {...(customProps as any)} />
    </Suspense>
  );
};

export default SliderLayouts;
